import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import type { RequestConfig } from "@app/config/axios/http";
import type { PaginationReturns } from "@src/api/types/PaginationReturns";
import type {
    ApiLeaveLimitCard,
    ApiLeaveLimitCardFilter,
    ApiLimitCard,
    ApiLimitCardContent,
    ApiLimitCardContentsFilter,
    ApiLimitCardFilter,
} from "./types";

interface LimitCardsListResponse {
    limitCards: ApiLimitCard[];
    totalQtyItems?: number;
}

interface LimitCardContentsListResponse {
    contents: ApiLimitCardContent[];
    totalQtyItems?: number;
}

interface LeavesLimitCardListResponse {
    leaves: ApiLeaveLimitCard[];
    totalQtyItems?: number;
}

/**
 * Транспортный слой OMP — лимитные карты: заголовок, состав (`content`), отпуска (`leave`).
 */
export const limitCardsApi = {
    /**
     * Загружает заголовки ЛК по фильтру.
     * @param params.flt фильтр {@link ApiLimitCardFilter}
     * @param params.offset смещение для пагинации
     * @param params.limit размер страницы
     * @param config опции HTTP (`signal`, заголовки)
     * @returns страница wire-объектов и `totalQtyItems`
     */
    load: async (
        params: {
            flt?: ApiLimitCardFilter;
            offset?: number;
            limit?: number;
        },
        config?: RequestConfig,
    ): Promise<PaginationReturns<ApiLimitCard>> => {
        const data = await ompApiClient.postOmega<LimitCardsListResponse>("stock/limitCards.loadByFilter", params, config);
        return {
            items: data.limitCards,
            totalQtyItems: data.totalQtyItems ?? 0,
        };
    },

    content: {
        /**
         * Загружает строки состава ЛК.
         * @param params.flt фильтр {@link ApiLimitCardContentsFilter}
         * @param config опции HTTP
         */
        load: async (
            params: {
                flt?: ApiLimitCardContentsFilter;
            },
            config?: RequestConfig,
        ): Promise<PaginationReturns<ApiLimitCardContent>> => {
            const data = await ompApiClient.postOmega<LimitCardContentsListResponse>(
                "stock/limitCards/contents.loadByFilter",
                params,
                config,
            );
            return {
                items: data.contents,
                totalQtyItems: data.totalQtyItems ?? 0,
            };
        },
    },

    leave: {
        /**
         * Загружает отпуска по ЛК (БО 1033).
         * @param params.flt фильтр {@link ApiLeaveLimitCardFilter}
         * @param params.offset смещение
         * @param params.limit лимит
         * @param config опции HTTP
         */
        load: async (
            params: {
                flt?: ApiLeaveLimitCardFilter;
                offset?: number;
                limit?: number;
            },
            config?: RequestConfig,
        ): Promise<PaginationReturns<ApiLeaveLimitCard>> => {
            const data = await ompApiClient.postOmega<LeavesLimitCardListResponse>(
                "stock/limitCards/leaves.loadByFilter",
                params,
                config,
            );
            return {
                items: data.leaves,
                totalQtyItems: data.totalQtyItems ?? 0,
            };
        },
    },
} as const;
